import { useEffect, useLayoutEffect, useRef } from "react";
import { create } from "zustand";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import type { CovariateCategory, DashboardField } from "../types/contract";

// The four environmental sources are ON by default (they auto-collect); JD/EC/samples default ON
// only once the field actually has that data. Unchecking a source does NOT delete anything — the
// data stays in the system, marked "inactive" — but an inactive source disables the trained-AI
// mapping path (partial data -> manual weights REQUIRED).

export type SourceKey =
  "jd" | "ec" | "samples" | "ssurgo" | "satellite" | "topography" | "climate";

export interface SourceDef {
  key: SourceKey;
  label: string;
  category: CovariateCategory | null; // covariate category the source yields (null = samples)
  manual: boolean; // user-initiated collection (vs auto-collected environmental)
  fallbackEstimate: string; // layer estimate before the catalog can be counted
}

export const SOURCE_DEFS: SourceDef[] = [
  {
    key: "jd",
    label: "Yield & Machine Data (JD Ops)",
    category: "machine",
    manual: true,
    fallbackEstimate: "~240 layers",
  },
  {
    key: "ec",
    label: "EC Scan",
    category: "ec",
    manual: true,
    fallbackEstimate: "~4 layers",
  },
  {
    key: "samples",
    label: "Soil Samples",
    category: null,
    manual: true,
    fallbackEstimate: "~10 layers",
  },
  {
    key: "ssurgo",
    label: "SSURGO Survey",
    category: "ssurgo",
    manual: false,
    fallbackEstimate: "~90 layers",
  },
  {
    key: "satellite",
    label: "Sat. Imagery (NDVI)",
    category: "satellite",
    manual: false,
    fallbackEstimate: "~450 layers",
  },
  {
    key: "topography",
    label: "Topography",
    category: "topography",
    manual: false,
    fallbackEstimate: "~25 layers",
  },
  {
    key: "climate",
    label: "Precip/Sunlight",
    category: "climate",
    manual: false,
    fallbackEstimate: "~130 layers",
  },
];

// Default activity when the user hasn't touched the checkbox: environmental sources are
// always on; manual sources turn on once their data exists on the field.
export function defaultActive(field: DashboardField, key: SourceKey): boolean {
  switch (key) {
    case "jd":
      return field.has_yield || field.has_jd_link;
    case "ec":
      return field.has_ec;
    case "samples":
      return (field.soil_samples?.collected ?? 0) > 0;
    default:
      return true;
  }
}

const STORE_KEY = "sm_data_sources";
type Overrides = Record<string, Partial<Record<SourceKey, boolean>>>; // fieldId -> key -> active

function loadOverrides(): Overrides {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}") as Overrides;
  } catch {
    return {};
  }
}

interface DataSourcesState {
  overrides: Overrides;
  setActive: (fieldId: string, key: SourceKey, active: boolean) => void;
  purgeField: (fieldId: string) => void;
}

export const useDataSources = create<DataSourcesState>((set) => ({
  overrides: loadOverrides(),
  setActive: (fieldId, key, active) =>
    set((s) => {
      const next: Overrides = {
        ...s.overrides,
        [fieldId]: { ...s.overrides[fieldId], [key]: active },
      };
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(next));
      } catch {
        /* private mode / quota */
      }
      return { overrides: next };
    }),
  purgeField: (fieldId) =>
    set((s) => {
      if (!(fieldId in s.overrides)) return s;
      const next: Overrides = { ...s.overrides };
      delete next[fieldId];
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify(next));
      } catch {
        /* private mode / quota */
      }
      return { overrides: next };
    }),
}));

export function isActive(
  overrides: Overrides,
  field: DashboardField,
  key: SourceKey,
): boolean {
  return overrides[field.field_id]?.[key] ?? defaultActive(field, key);
}

// Sources the user explicitly deactivated (data stays, marked inactive). Only sources
// that WOULD be active (auto, or manual with data) count — switching off a never-populated
// manual source deactivates nothing. Any entry here forces the manual-weights mapping path.
export function inactiveSources(
  overrides: Overrides,
  field: DashboardField,
): SourceDef[] {
  return SOURCE_DEFS.filter(
    (d) => defaultActive(field, d.key) && !isActive(overrides, field, d.key),
  );
}

// On mount we hydrate the local store from the merged map (server wins over any stale localStorage)
// toggles PATCH the server and roll back locally on failure.

// Seed local overrides from the server's merged map: an override exists exactly where the
// server value differs from the (identical) data-aware default.
export function hydrateFromServer(
  field: DashboardField,
  map: Record<string, boolean>,
): void {
  const derived: Partial<Record<SourceKey, boolean>> = {};
  for (const d of SOURCE_DEFS) {
    const v = map[d.key];
    if (typeof v === "boolean" && v !== defaultActive(field, d.key))
      derived[d.key] = v;
  }
  useDataSources.setState((s) => {
    const next = { ...s.overrides, [field.field_id]: derived };
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    return { overrides: next };
  });
}

// Local set + server PATCH; the PATCH response is the authoritative merged map (re-hydrate
// from it); revert on failure so the UI never lies about persistence.
export function setActiveSynced(
  field: DashboardField,
  key: SourceKey,
  active: boolean,
): void {
  const prev = useDataSources.getState().overrides[field.field_id]?.[key];
  useDataSources.getState().setActive(field.field_id, key, active);
  api
    .patchDataSource(field.field_id, key, active)
    .then((map) => hydrateFromServer(field, map))
    .catch((e) => {
      console.warn("[data-sources] PATCH failed — reverting", e);
      useDataSources
        .getState()
        .setActive(field.field_id, key, prev ?? defaultActive(field, key));
    });
}

// Fetch + hydrate once per field (react-query dedupes across the cell + soilprops panel).
export function useServerDataSources(field: DashboardField): void {
  const { data } = useQuery({
    queryKey: ["data-sources", field.field_id],
    queryFn: () => api.fetchDataSources(field.field_id),
    staleTime: 60_000,
  });
  // The row object is a new identity on most renders. Re-hydrate only when the
  // field id or the server map changes, and read the row that render held.
  const fieldRef = useRef(field);
  useLayoutEffect(() => {
    fieldRef.current = field;
  }, [field]);
  useEffect(() => {
    if (data) hydrateFromServer(fieldRef.current, data);
  }, [data, field.field_id]);
}
